import { NegativeValueFinding } from '../data-qa-types';
import { describeNegativeFinding, isBlockingFinding } from '../negative-values-utils';
import { InlineAlert } from './InlineAlert';

export interface LabeledNegativeFinding {
  finding: NegativeValueFinding;
  // Column label, already resolved to a feature name / stage where possible.
  label: string;
}

interface NegativeValuesAlertProps {
  // Only the active tab's findings (see findingsForSection).
  findings: LabeledNegativeFinding[];
  // The model's training period in words ("week") -- see periodUnitFor.
  periodUnit: string;
  // Declared paid-media columns the check couldn't see (Channels tab only).
  missingColumnCount?: number;
}

export const NegativeValuesAlert = ({
  findings,
  periodUnit,
  missingColumnCount = 0
}: NegativeValuesAlertProps) => {
  const blocking = findings.filter(f => isBlockingFinding(f.finding));
  const warnings = findings.filter(f => !isBlockingFinding(f.finding));

  return (
    <>
      {blocking.length > 0 && (
        <InlineAlert variant='error' title='Negative values will break model training'>
          These sum negative over at least one {periodUnit} inside the model&rsquo;s fencepost
          window &mdash; the per-{periodUnit} totals the model actually trains on. This section
          can&rsquo;t be approved until the data is fixed at the source and reloaded.
          <FindingList findings={blocking} periodUnit={periodUnit} />
        </InlineAlert>
      )}
      {warnings.length > 0 && (
        <InlineAlert variant='warning' title='Negative values to check'>
          These won&rsquo;t break this model run: either the negative days are outweighed within
          their {periodUnit}, or the negative {periodUnit}s fall outside the fencepost window.
          Confirm they&rsquo;re expected (e.g. refunds or platform adjustments), and fix them before
          a refresh widens the window.
          <FindingList findings={warnings} periodUnit={periodUnit} />
        </InlineAlert>
      )}
      {missingColumnCount > 0 && (
        <InlineAlert variant='info' title='Some paid-media columns weren’t checked for negatives'>
          {missingColumnCount} declared paid-media column{missingColumnCount === 1 ? '' : 's'}{' '}
          {missingColumnCount === 1 ? 'isn’t' : 'aren’t'} in this model&rsquo;s data frame, so the
          negative-value check couldn&rsquo;t see {missingColumnCount === 1 ? 'it' : 'them'}. Check{' '}
          {missingColumnCount === 1 ? 'it' : 'them'} for negative values before training.
        </InlineAlert>
      )}
    </>
  );
};

interface FindingListProps {
  findings: LabeledNegativeFinding[];
  periodUnit: string;
}

const FindingList = ({ findings, periodUnit }: FindingListProps) => (
  <ul className='data-qa-negative-values__list'>
    {findings.map(({ finding, label }) => (
      <li key={`${label}|${finding.column}`}>
        <strong>{label}</strong>: {describeNegativeFinding(finding, periodUnit)}
      </li>
    ))}
  </ul>
);
